import { useState, useEffect } from "react";
import { useLanguage, API } from "../App";
import { motion } from "framer-motion";
import axios from "axios";
import { Calendar, ArrowRight, Tag, Clock } from "lucide-react";
import { Badge } from "./ui/badge";

const NewsCard = ({ article, language }) => {
  const readLabel = language === "es" ? "Leer el artículo" : language === "en" ? "Read the article" : "Lire l'article";
  return (
    <a href={`/blog/${article.slug || article.id}`}>
      <motion.div
        whileHover={{ y: -5 }}
        className="bg-white rounded-2xl shadow-md overflow-hidden cursor-pointer card-hover group h-full"
        data-testid={`news-${article.id}`}
      >
        {article.image && (
          <div className="h-48 overflow-hidden">
            <img src={article.image} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          </div>
        )}
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <Badge variant="secondary" className="text-xs">
              <Tag size={12} className="mr-1" />
              {article.category}
            </Badge>
            <div className="flex items-center gap-3 text-slate-400 text-sm">
              {article.reading && (
                <span className="flex items-center"><Clock size={12} className="mr-1" />{article.reading}</span>
              )}
              <span className="flex items-center"><Calendar size={14} className="mr-1" />{new Date(article.date).toLocaleDateString()}</span>
            </div>
          </div>
          <h3 className="font-playfair text-xl font-bold text-slate-900 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors">{article.title}</h3>
          <p className="text-slate-600 text-sm line-clamp-3 mb-4">{article.excerpt}</p>
          <div className="flex items-center text-blue-600 font-medium text-sm group-hover:gap-2 transition-all" data-testid="news-read-cta">
            {readLabel}
            <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </motion.div>
    </a>
  );
};

export const News = () => {
  const { language, t } = useLanguage();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(`${API}/news/${language}`);
        setNews(response.data.news);
      } catch (error) {
        console.error("Error fetching news:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, [language]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/3 mx-auto" />
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-64 bg-slate-200 rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="news-section">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-12"
      >
        <h2 className="font-playfair text-3xl md:text-4xl font-bold text-slate-900 mb-4">
          {t.news.title}
        </h2>
        <p className="text-slate-600 text-lg">{t.news.subtitle}</p>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-6">
        {news.map((article, index) => (
          <motion.div
            key={article.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
          >
            <NewsCard article={article} language={language} />
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default News;
